// lodash
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
// jl-design-system
import env from 'jl-design-system/utils/env/env';
import validate from 'jl-design-system/form/validation/validate';
// constants
import {
  ANALYTICS_OPEN_APPLE_PAY_PAYMENT_SHEET,
  APPLE_PAY_BILLING_ADDRESS_MISSING,
  APPLE_PAY_PAYMENT,
  APPLE_PAY_PAYMENT_CANCEL,
  APPLE_PAY_PAYMENT_FAILURE,
  APPLE_PAY_REQUEST_PAYMENT_SESSION,
  APPLE_PAY_SET_CARD_TYPE,
  APPLE_PAY_SUBMIT_PAYMENT_DATA,
  SET_APPLE_PAY_PAYMENT_REQUEST,
  SET_CAN_MAKE_APPLE_PAY_PAYMENTS,
  UPDATE_BILLING_ADDRESS,
} from '../../../constants/actionConstants';
import errorCodeConstants from '../../../constants/errorCodeConstants';
import { isApps, isIosApp, isWeb } from '../../reducers/app/appReducer';
import {
  getBillingAddress,
  getFormattedAddressFromApplePay,
  mapAddressValidationErrorToApplePayErrors,
} from '../../../utils/address/addressHelpers';

import {
  URL_CREATE_APPLE_PAY_PAYMENT,
  URL_OCP_SUBMIT_CARD_DETAILS,
  URL_REQUEST_APPLE_PAY_SESSION,
} from '../../../constants/endpointConstants';
import { updateBillingAddress } from './cardPaymentActions';
import {
  setPaymentProcessing,
  setSelectedPaymentType,
  submitPayment,
  triggerTransactionStatusAnalytics,
} from './paymentActions';
import { submitOrderAndRedirect } from './submitOrderActions';
import { triggerAnalyticsEvent } from '../analytics/analyticsAction';
import paymentStatusConstants from '../../../constants/paymentStatusConstants';
import getBillingDetailsFormConfig from '../../../utils/form/configs/billingAddress';
import { initPaymentPage } from './orderFormActions';
import { sendNewRelicCustomEvent } from '../../../utils/logging/logging-utils';
import featureConstants from '../../../constants/featureConstants';
import paymentTypeConstants from '../../../constants/paymentTypeConstants';
import { isFeatureActive } from '../../reducers/config/configReducer';

const canMakeApplePayPaymentsOnWeb = async (state) => {
  const applePayVersion = get(state.config.applePayConfiguration, 'applePayMethod.data.version');
  const merchantIdentifier = get(state.config.applePayConfiguration, 'applePayMethod.data.merchantIdentifier');
  if (window.ApplePaySession && window.ApplePaySession.supportsVersion(applePayVersion)) {
    return window.ApplePaySession.canMakePaymentsWithActiveCard(merchantIdentifier);
  }
  return false;
};

export const setCanMakeApplePayPayments = () => ({
  type: SET_CAN_MAKE_APPLE_PAY_PAYMENTS,
});

export const setApplePayCardType = (cardType = '') => async (dispatch, getState) => {
  const state = getState();
  const billingAddress = state.payment?.billingAddress;
  const nameOnCard = `${billingAddress.addressee?.firstName} ${billingAddress.addressee?.lastName}`;

  return dispatch({
    type: APPLE_PAY_SET_CARD_TYPE,
    request: client => client({
      path: URL_CREATE_APPLE_PAY_PAYMENT,
      config: {
        method: 'PATCH',
        body: {
          cardType,
          nameOnCard,
        },
      },
    }),
  });
};

export const cancelApplePayPayment = () => (dispatch, getState) => {
  if (isFeatureActive(getState(), featureConstants.LOG_APPLE_PAY_DATA)) {
    /* eslint-disable-next-line no-console */
    console.log(APPLE_PAY_PAYMENT_CANCEL);
  }
  dispatch({ type: APPLE_PAY_PAYMENT_CANCEL });
  dispatch(initPaymentPage());
};

export const failureApplePayPayment = () => (dispatch, getState) => {
  if (isFeatureActive(getState(), featureConstants.LOG_APPLE_PAY_DATA)) {
    /* eslint-disable-next-line no-console */
    console.log(APPLE_PAY_PAYMENT_FAILURE);
  }
  dispatch({ type: APPLE_PAY_PAYMENT_FAILURE });
  dispatch(initPaymentPage());
};

export const canMakeApplePayPayments = () => async (dispatch, getState) => {
  const state = getState();
  const isEligbleApplePayCustomer = !isApps(state) || (isApps(state) && !state?.user?.isGuest);
  const applePayCompatibilityChecksComplete = get(state.payment, 'applePayCompatibilityChecksComplete');

  if (applePayCompatibilityChecksComplete ||
    !isFeatureActive(state, featureConstants.APPLE_PAY) ||
    (isFeatureActive(state, featureConstants.APPLE_PAY_DISABLED_FOR_APPS_GUESTS) && !isEligbleApplePayCustomer)) {
    return;
  }

  if (isWeb(state) && await canMakeApplePayPaymentsOnWeb(state).catch(() => false)) {
    dispatch(setCanMakeApplePayPayments());
  } else if (isIosApp(state)) {
    try {
      const supportedNetworks = get(state.config.applePayConfiguration, 'applePayMethod.data.supportedNetworks');
      const canMakeApplePayPaymentsUsingNetworksHandler = get(window.webkit.messageHandlers,
        'canMakeApplePayPaymentsUsingNetworksHandler');
      if (canMakeApplePayPaymentsUsingNetworksHandler) {
        canMakeApplePayPaymentsUsingNetworksHandler.postMessage(supportedNetworks);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Error: canMakeApplePayPaymentsUsingNetworksHandler.postMessage()', err);
    }
  }
};

const requestPaymentSession = (body, hostUrl, headers) => ({
  type: APPLE_PAY_REQUEST_PAYMENT_SESSION,
  request: client => client({
    path: URL_REQUEST_APPLE_PAY_SESSION,
    config: {
      method: 'POST',
      headers,
      body,
    },
    hostUrl,
  }),
});

const submitApplePayPaymentData = (paymentId, body, hostUrl, headers) => ({
  type: APPLE_PAY_SUBMIT_PAYMENT_DATA,
  request: client => client({
    path: URL_OCP_SUBMIT_CARD_DETAILS(paymentId),
    config: {
      method: 'PATCH',
      headers,
      body,
    },
    hostUrl,
  }),
});

const merchantSessionPromise = (event, dispatch, state) => new Promise(
  async (resolve, reject) => {
    const host = get(state.orderForm, 'ocpIntegration.baseUrl');
    const sessionId = get(state, 'bff.sessionId', '');
    const orderFormId = get(state, 'orderForm.id', '');
    const omsOrderId = get(state, 'orderForm.omsOrderId', '');
    const {
      displayName,
      initiative,
      initiativeContext,
      merchantIdentifier,
    } = get(state.config, 'applePayConfiguration.merchantValidationData', {});

    const body = {
      displayName,
      initiative,
      initiativeContext,
      merchantIdentifier,
      validationUrl: event.validationURL,
    };

    const headers = {
      JSESSIONID: sessionId,
      'JL-BASKET-ID': orderFormId,
      'JL-OMS-ORDER-ID': omsOrderId,
      'JL-CALLING-APP-V1': 'OCC',
      'Accept-Language': '*',
    };

    const postLocation = `${host}/${URL_REQUEST_APPLE_PAY_SESSION}`;

    const { type, result = {}, error = {} } = await dispatch(requestPaymentSession(body, host, headers));

    if (type === `${APPLE_PAY_REQUEST_PAYMENT_SESSION}.SUCCESS`) {
      sendNewRelicCustomEvent('applePaySessionResponse', {
        statusCode: 200,
        orderFormId,
        postLocation,
        sessionId,
      });
      resolve(result);
    } else {
      sendNewRelicCustomEvent('applePaySessionResponse', {
        ...error,
        orderFormId,
        postLocation,
        sessionId,
      });
      dispatch({ type: APPLE_PAY_PAYMENT_FAILURE });
      reject();
    }
  },
);

const submitApplePayPayment = (billingAddress, paymentResponse) => async (dispatch, getState) => {
  const shouldRequestBillingAddressFromApplePay = !isApps(getState()) &&
    isFeatureActive(getState(), featureConstants.REQUEST_BILLING_ADDRESS_FROM_APPLE_PAY);

  if (billingAddress) {
    const validateAddressResult = validate({
      ...billingAddress.address,
      ...billingAddress.addressee,
      phoneNumber: billingAddress.phoneNumber,
    }, {
      config: getBillingDetailsFormConfig({
        countryCode: billingAddress.address?.countryCode,
        actions: {},
        configExtras: {
          titleFieldOptional: true,
        },
        enableGBCounty: !!billingAddress.address?.countyStateOrProvince,
      }),
    });

    if (!isEmpty(validateAddressResult)) {
      return {
        validateAddressResult,
      };
    }

    const { type } = await dispatch(updateBillingAddress(
      billingAddress,
      null,
      shouldRequestBillingAddressFromApplePay,
      true,
    ));

    if (type === `${UPDATE_BILLING_ADDRESS}.FAILED`) {
      if (isFeatureActive(getState(), featureConstants.LOG_APPLE_PAY_DATA)) {
        /* eslint-disable-next-line no-console */
        console.log(`${UPDATE_BILLING_ADDRESS}.FAILED`);
      }
      return {};
    }
  }

  const createApplePayPaymentResponse = await dispatch({
    type: APPLE_PAY_PAYMENT,
    request: client => client({
      path: URL_CREATE_APPLE_PAY_PAYMENT,
      config: { method: 'POST' },
    }),
  });

  if (!isApps(getState()) && createApplePayPaymentResponse.type === `${APPLE_PAY_PAYMENT}.SUCCESS`) {
    const setCardTypeResponse =
      await dispatch(setApplePayCardType(paymentResponse?.details?.token?.paymentMethod?.network));
    if (setCardTypeResponse.type === `${APPLE_PAY_SET_CARD_TYPE}.FAILED`) {
      return setCardTypeResponse;
    }
  }

  return createApplePayPaymentResponse;
};

const triggerOpenApplePayPaymentSheetEvent = () => async (dispatch, getState) => {
  dispatch(triggerAnalyticsEvent(ANALYTICS_OPEN_APPLE_PAY_PAYMENT_SHEET, {
    payment: {
      payment: {
        ...get(getState().analytics, 'analyticsData.checkout.payment.order'),
      },
    },
  }));
};

const submitApplePayToken = token => async (dispatch, getState) => {
  const state = getState();
  const paymentId = get(state.payment, 'ocpPaymentId');
  const host = get(state.orderForm, 'ocpIntegration.baseUrl');
  const sessionId = get(state, 'bff.sessionId', '');
  const orderFormId = get(state, 'orderForm.id', '');
  const omsOrderId = get(state, 'orderForm.omsOrderId', '');
  const body = {
    method: 'applePay',
    applePayData: window.btoa(JSON.stringify(token)),
  };

  const headers = {
    JSESSIONID: sessionId,
    'JL-BASKET-ID': orderFormId,
    'JL-OMS-ORDER-ID': omsOrderId,
    'JL-CALLING-APP-V1': 'OCC',
    Accept: 'application/vnd.jl-payments-v3+json',
    'Content-Type': 'application/vnd.jl-payments-v3+json',
    'Accept-Language': '*',
  };

  const submitAction = await dispatch(submitApplePayPaymentData(paymentId, body, host, headers));

  const {
    type,
    error: { code, authenticationStatus: authenticationStatusOnError } = {},
    result: { status, authenticationStatus: authenticationStatusOnSuccess } = {},
  } = submitAction;

  if (type === `${APPLE_PAY_SUBMIT_PAYMENT_DATA}.SUCCESS`) {
    dispatch(triggerTransactionStatusAnalytics(status, { authenticationStatus: authenticationStatusOnSuccess }));
  }

  if (type === `${APPLE_PAY_SUBMIT_PAYMENT_DATA}.FAILED`) {
    dispatch(triggerTransactionStatusAnalytics('FAILED', {
      errorCode: code,
      authenticationStatus: authenticationStatusOnError,
    }));
  }

  return submitAction;
};

export const submitApplePayTokenAndOrder = token => async (dispatch) => {
  const { type, result: { status } = {} } = await dispatch(submitApplePayToken(token));

  if (type === `${APPLE_PAY_SUBMIT_PAYMENT_DATA}.SUCCESS`
    && status === paymentStatusConstants.PENDING_AUTHORISATION) {
    dispatch(setPaymentProcessing(false));
    dispatch(submitOrderAndRedirect());
  } else {
    dispatch(failureApplePayPayment());
  }
};

export const handlePaymentResponse = (response, isResubmitting = false) => async (dispatch, getState) => {
  const applePayPaymentRequest = response || getState().payment.applePayPaymentRequest;
  const billingContact = get(applePayPaymentRequest, 'details.billingContact');
  const payerPhone = get(applePayPaymentRequest, 'payerPhone');

  if (response) {
    await dispatch({
      type: SET_APPLE_PAY_PAYMENT_REQUEST,
      paymentRequest: response,
    });
  }

  const shouldRequestBillingAddressFromApplePay = !isApps(getState()) &&
    isFeatureActive(getState(), featureConstants.REQUEST_BILLING_ADDRESS_FROM_APPLE_PAY);

  let billingAddress;

  if (
    shouldRequestBillingAddressFromApplePay &&
    !isEmpty(billingContact) &&
    payerPhone &&
    !isResubmitting
  ) {
    billingAddress = getFormattedAddressFromApplePay(billingContact, payerPhone);
  } else {
    billingAddress = getBillingAddress(getState());
  }

  if (!billingAddress || isEmpty(billingAddress)) {
    const sessionId = get(getState(), 'bff.sessionId', '');
    const orderFormId = get(getState(), 'orderForm.id', '');

    sendNewRelicCustomEvent('applePayBillingAddressMissing', {
      orderFormId,
      sessionId,
      isApps: isApps(getState()),
    });

    dispatch({
      type: APPLE_PAY_BILLING_ADDRESS_MISSING,
      error: {
        code: errorCodeConstants.CLIENT_APPLE_PAY_BILLING_ADDRESS_MISSING,
      },
    });
    return;
  }

  if (isFeatureActive(getState(), featureConstants.LOG_APPLE_PAY_DATA)) {
    /* eslint-disable-next-line no-console */
    console.log(billingAddress);
  }

  const submitApplePayPaymentResponse = await dispatch(submitApplePayPayment(billingAddress, applePayPaymentRequest));

  if (shouldRequestBillingAddressFromApplePay) {
    const errors = mapAddressValidationErrorToApplePayErrors(submitApplePayPaymentResponse.validateAddressResult);

    if (!isEmpty(errors?.paymentMethod)) {
      applePayPaymentRequest.retry(errors).then(() => {
        dispatch(handlePaymentResponse(applePayPaymentRequest));
      }).catch(() => {
        dispatch(cancelApplePayPayment());
      });

      return;
    }
  }

  const { type } = submitApplePayPaymentResponse;

  if (type === `${APPLE_PAY_SET_CARD_TYPE}.FAILED`) {
    dispatch(failureApplePayPayment());
    applePayPaymentRequest.complete('fail');
    return;
  }

  if (type === `${APPLE_PAY_PAYMENT}.SUCCESS`) {
    const { type, result: { status } = {} } = await dispatch(submitApplePayToken(applePayPaymentRequest.details.token));

    if (type === `${APPLE_PAY_SUBMIT_PAYMENT_DATA}.SUCCESS`
      && status === paymentStatusConstants.PENDING_AUTHORISATION) {
      await applePayPaymentRequest.complete('success');
      dispatch(setPaymentProcessing(false));
      dispatch(submitOrderAndRedirect());
      return;
    }
  }

  applePayPaymentRequest.complete('fail');
  dispatch(failureApplePayPayment());
};

const paymentRequestPayload = state => ({
  applePayMethod: get(state.config, 'applePayConfiguration.applePayMethod'),
  details: {
    total: {
      label: 'John Lewis',
      amount: {
        currency: 'GBP',
        value: get(state.orderForm, 'amounts.outstandingBalanceRaw'),
      },
    },
  },
  options: get(state.config, 'applePayOptions'),
});

const applePayPaymentInitOnWeb = () => async (dispatch, getState) => {
  try {
    const state = getState();
    const {
      applePayMethod,
      details,
      options,
    } = paymentRequestPayload(state);

    const paymentRequest = new window.PaymentRequest([applePayMethod], details, options);

    if (isFeatureActive(state, featureConstants.LOG_APPLE_PAY_DATA)) {
      /* eslint-disable-next-line no-console */
      console.log(JSON.stringify(paymentRequest));
    }

    paymentRequest.onmerchantvalidation = (event) => {
      event.complete(merchantSessionPromise(event, dispatch, state));
    };

    dispatch(triggerOpenApplePayPaymentSheetEvent());

    await paymentRequest.show()
      .then(async (response) => { await dispatch(handlePaymentResponse(response)); })
      .catch(() => {
        dispatch(cancelApplePayPayment());
      });
  } catch (error) {
    dispatch(failureApplePayPayment());
  }
};

const applePayPaymentInitOnIosApp = () => async (dispatch, getState) => {
  try {
    const billingAddress = getBillingAddress(getState());
    const { type = {} } = await dispatch(submitApplePayPayment(billingAddress));
    if (type === `${APPLE_PAY_PAYMENT}.SUCCESS`) {
      window.webkit.messageHandlers.sendApplePayPaymentRequest.postMessage(paymentRequestPayload(getState()));
      dispatch(triggerOpenApplePayPaymentSheetEvent());
    } else {
      dispatch(failureApplePayPayment());
    }
  } catch (error) {
    dispatch(failureApplePayPayment());
  }
};

export const applePayPaymentInit = () => async (dispatch, getState) => {
  const state = getState();

  if (isWeb(state)) {
    await dispatch(applePayPaymentInitOnWeb());
  } else if (isIosApp(state)) {
    await dispatch(applePayPaymentInitOnIosApp());
  }
};

export const handleExpressApplePayClick = () => async (dispatch) => {
  await dispatch(setSelectedPaymentType(paymentTypeConstants.APPLE_PAY));

  // timeout adding for stubbed environments to allow PaymentWalletStubs to mount
  if (env.isClientLocal || env.isClientDev) {
    setTimeout(() => {
      dispatch(submitPayment());
    }, 1000);
  } else {
    dispatch(submitPayment());
  }
};
